<script setup lang="ts">
import { reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Message } from '@arco-design/web-vue'

import { apiErrorText } from '@/shared/api/errorText'
import LanguageSwitch from '@/shared/components/LanguageSwitch.vue'
import { useAuthStore } from './store'

const auth = useAuthStore()
const router = useRouter()
const form = reactive({ oldPassword: '', newPassword: '', confirm: '' })
const error = ref('')
const saving = ref(false)
const { t } = useI18n()

async function submit() {
  if (saving.value) return
  if (form.newPassword !== form.confirm) {
    error.value = t('auth.mismatch')
    return
  }
  error.value = ''
  saving.value = true
  try {
    await auth.changePassword(form.oldPassword, form.newPassword)
    Message.success(t('auth.changeSuccess'))
    await router.replace('/login')
  } catch (cause) {
    error.value = apiErrorText(cause, t('auth.changeFailed'))
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <main class="login-page">
    <header class="password-header">
      <div class="password-brand">
        <svg viewBox="0 0 40 40" aria-hidden="true">
          <defs>
            <linearGradient id="passwordBrandGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#3F7BFF" /><stop offset="1" stop-color="#0FA6C2" />
            </linearGradient>
          </defs>
          <rect width="40" height="40" rx="11" fill="url(#passwordBrandGradient)" />
          <g transform="translate(20,20)" stroke="#fff" stroke-width="2.6" stroke-linecap="round">
            <path d="M0-9.5V9.5M-8.2-4.75L8.2 4.75M-8.2 4.75L8.2-4.75" />
          </g>
        </svg>
        <div>
          <strong>{{ t('auth.brand') }}</strong>
          <span>{{ t('auth.brandEn') }}</span>
        </div>
      </div>
      <LanguageSwitch />
    </header>

    <div class="password-content">
      <section class="login-card" aria-labelledby="password-title">
        <div class="password-intro">
          <div class="password-security-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <rect x="5" y="10" width="14" height="11" rx="3" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3" />
            </svg>
          </div>
          <h1 id="password-title">{{ t('shell.changePassword') }}</h1>
          <p>{{ t('auth.personalPasswordHint') }}</p>
        </div>

        <a-alert v-if="error" type="error" class="password-error" role="alert">{{ error }}</a-alert>
        <a-form :model="form" class="password-form" layout="vertical" @submit-success="submit">
          <a-form-item field="oldPassword" :label="t('auth.currentPassword')"
            :rules="[{ required: true, message: t('common.required', { label: t('auth.currentPassword') }) }]">
            <a-input-password v-model="form.oldPassword" size="large" autocomplete="current-password"
              :input-attrs="{ id: 'oldPassword', 'aria-label': t('auth.currentPassword') }" :placeholder="t('auth.currentPasswordPlaceholder')" />
          </a-form-item>
          <a-form-item field="newPassword" :label="t('auth.newPassword')"
            :rules="[{ required: true, message: t('common.required', { label: t('auth.newPassword') }) }]">
            <a-input-password v-model="form.newPassword" size="large" autocomplete="new-password"
              :input-attrs="{ id: 'newPassword', 'aria-label': t('auth.newPassword') }" :placeholder="t('auth.newPasswordPlaceholder')" />
          </a-form-item>
          <a-form-item field="confirm" :label="t('auth.confirmPassword')"
            :rules="[{ required: true, message: t('common.required', { label: t('auth.confirmPassword') }) }]">
            <a-input-password v-model="form.confirm" size="large" autocomplete="new-password"
              :input-attrs="{ id: 'confirm', 'aria-label': t('auth.confirmPassword') }" :placeholder="t('auth.confirmPasswordPlaceholder')" />
          </a-form-item>
          <a-button html-type="submit" type="primary" size="large" long :loading="saving">{{ t('auth.saveAndLogin') }}</a-button>
        </a-form>
        <p class="password-session-note">{{ t('auth.passwordSessionNote') }}</p>
      </section>
    </div>

    <footer class="password-footer">{{ t('auth.footer') }}</footer>
  </main>
</template>

<style scoped>
.login-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 28px 32px 20px;
  background: radial-gradient(ellipse at 15% 15%, #e9f3ff 0%, transparent 55%),
    radial-gradient(ellipse at 90% 90%, #e9f8f7 0%, transparent 50%), var(--cw-color-bg);
}

.password-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
}

.password-brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.password-brand svg {
  width: 36px;
  height: 36px;
  flex: none;
}

.password-brand strong,
.password-brand span {
  display: block;
}

.password-brand strong {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
}

.password-brand span {
  margin-top: 3px;
  font-size: 11px;
  color: var(--cw-color-text-muted);
  letter-spacing: .4px;
}

.password-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 36px 0;
}

.login-card {
  width: 100%;
  max-width: 480px;
  min-width: 0;
  padding: 36px;
  border: 1px solid var(--cw-color-border);
  border-radius: 20px;
  background: var(--cw-color-surface);
  box-shadow: 0 16px 48px rgba(35, 64, 110, .07);
}

.password-intro {
  margin-bottom: 28px;
  text-align: center;
}

.password-security-icon {
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  margin: 0 auto 18px;
  border: 1px solid #dce9ff;
  border-radius: 16px;
  background: #f0f6ff;
  color: var(--cw-color-primary);
}

.password-security-icon svg {
  width: 28px;
  height: 28px;
}

.password-intro h1 {
  margin: 0 0 10px;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.password-intro p,
.password-session-note {
  margin: 0;
  color: var(--cw-color-text-muted);
  line-height: 1.7;
  overflow-wrap: anywhere;
}

.password-error {
  margin-bottom: 20px;
}

.password-form :deep(.arco-form-item) {
  margin-bottom: 20px;
}

.password-form :deep(.arco-input-wrapper) {
  min-height: 44px;
  border: 1px solid var(--cw-color-border);
  border-radius: 8px;
  background: var(--cw-color-surface);
}

.password-form :deep(.arco-input-focus) {
  border-color: var(--cw-color-primary);
}

.password-form :deep(.arco-input-error) {
  border-color: var(--cw-color-danger);
}

.password-form :deep(.arco-form-item-extra) {
  margin-top: 8px;
  color: var(--cw-color-text-muted);
  font-size: 12px;
  line-height: 1.65;
}

.password-form :deep(.arco-btn) {
  min-height: 44px;
  border-radius: 8px;
}

.password-session-note {
  margin-top: 18px;
  font-size: 12px;
  text-align: center;
}

.password-footer {
  color: var(--cw-color-text-muted);
  font-size: 11px;
  line-height: 1.6;
  text-align: center;
}

@media (max-width: 600px) {
  .login-page { padding: 20px 16px 16px; }
  .password-content { padding: 28px 0; }
  .login-card { padding: 28px 24px; border-radius: 16px; }
  .password-intro h1 { font-size: 22px; }
  .password-form :deep(input) { font-size: 16px; }
}
</style>
